import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitEventSignup, type EventRow, formatEventDate, formatEventTime } from "@/lib/events";
import { useSignupSummary } from "@/hooks/useEvents";
import { handleApiError } from "@/lib/errors";
import { trackEventSignup } from "@/lib/analytics";

interface EventSignupModalProps {
  event: EventRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EventSignupModal = ({ event, open, onOpenChange }: EventSignupModalProps) => {
  const queryClient = useQueryClient();
  const { data: summary } = useSignupSummary(event.id, open);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [numAttendees, setNumAttendees] = useState("1");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const taken = summary?.taken ?? 0;
  const capacity = summary?.capacity ?? null;
  const isFull = summary?.is_full ?? false;
  const seatsLeft = capacity != null ? Math.max(0, capacity - taken) : null;

  function reset() {
    setName("");
    setEmail("");
    setPhone("");
    setNumAttendees("1");
    setMessage("");
    setSubmitted(false);
  }

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) {
      // Slight delay so the closing animation doesn't show empty fields.
      setTimeout(reset, 250);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const num = Number(numAttendees) || 1;
      await submitEventSignup({
        event_id: event.id,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        num_attendees: num,
        message: message.trim() || undefined,
      });
      trackEventSignup({ eventId: event.id, numAttendees: num });
      setSubmitted(true);
      void queryClient.invalidateQueries({ queryKey: ["event-signup-summary", event.id] });
    } catch (err) {
      handleApiError(err, "Kunne ikke registrere påmeldingen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{event.title}</DialogTitle>
          <DialogDescription>
            {formatEventDate(event.starts_at)} kl. {formatEventTime(event.starts_at)}
            {event.location ? ` · ${event.location}` : ""}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary" />
            <h3 className="text-lg font-semibold">Påmelding mottatt</h3>
            <p className="text-sm text-muted-foreground">
              Vi sendte en bekreftelse til {email}. Du hører fra oss så snart Håp Naturlab har sett over.
            </p>
            <Button onClick={() => handleOpenChange(false)}>Lukk</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {capacity != null && (
              <p className="text-sm text-muted-foreground">
                {isFull
                  ? "Arrangementet er fullt."
                  : `${seatsLeft} av ${capacity} plasser igjen`}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="signup-name">Navn *</Label>
              <Input
                id="signup-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting || isFull}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="signup-email">E-post *</Label>
                <Input
                  id="signup-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting || isFull}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-phone">Telefon</Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={submitting || isFull}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="signup-attendees">Antall plasser</Label>
              <Input
                id="signup-attendees"
                type="number"
                min={1}
                max={Math.min(seatsLeft ?? 50, 50)}
                value={numAttendees}
                onChange={(e) => setNumAttendees(e.target.value)}
                disabled={submitting || isFull}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signup-message">Melding (valgfritt)</Label>
              <Textarea
                id="signup-message"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={submitting || isFull}
                placeholder="Allergier, ønsker eller annet vi bør vite"
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting || isFull}>
              {isFull ? "Fullt" : submitting ? "Sender …" : "Meld meg på"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventSignupModal;
